import React, { useState, useEffect, useMemo, useRef } from "react";
import { 
  Cpu, Box, Layers, Zap, Play, Check, Copy, Download, RefreshCw, 
  ExternalLink, Sparkles, Sliders, ShieldCheck, Thermometer, Wind, 
  Radio, HardDrive, Eye, RotateCw, Settings, FileCode, CheckCircle2, 
  AlertTriangle, ArrowRight, CornerDownRight, Share2, Compass, Printer, Scale,
  Palette, Droplets, FolderKanban, Bookmark, MessageSquare, Bot, Smartphone
} from "lucide-react";
import { toast } from "sonner";
import { PhotogrammetryBridge } from "./PhotogrammetryBridge";

// Model presets interface
interface CADModelPreset {
  id: string;
  name: string;
  description: string;
  category: "mechanical" | "enclosure" | "electronics" | "robotics";
  defaultParams: Record<string, number>;
  paramLabels: Record<string, { label: string; unit: string; min: number; max: number; step: number }>;
  generateFusion: (p: Record<string, number>) => string;
  generateBlender: (p: Record<string, number>) => string;
  generateFreeCAD: (p: Record<string, number>) => string;
  generateKiCad?: (p: Record<string, number>) => string;
  get3DPoints: (p: Record<string, number>) => { vertices: [number, number, number][]; edges: [number, number][] };
}

// Pre-configured CAD Models
const CAD_PRESETS: CADModelPreset[] = [
  {
    id: "mesa_l_htx",
    name: "Mesa en L Completa (72\" x 72\" HTX Studio + Engranajes 24V)",
    description: "Mesa completa (72\"x72\"x28\"): Cubierta 1 Izq (72\"x28\"), Cubierta 2 Retorno Der (44\"x28\" con bahía celular CoreXY Qi2 de 15\"x22\"), chasis PTR 2\"x1.5\", 6 patas telescópicas y sistema opcional Carpeta 05 (Cajas de Engranajes Cónicos 90° + Husillos ACME TR16x4 + Tuercas de Bronce + Motores 24V).",
    category: "mechanical",
    defaultParams: {
      wingLengthIn: 72,
      depthIn: 28,
      deskHeightIn: 28,
      enableHTX: 1
    },
    paramLabels: {
      wingLengthIn: { label: "Largo de Alas (Inches)", unit: "in", min: 48, max: 96, step: 2 },
      depthIn: { label: "Profundidad de Cubiertas", unit: "in", min: 24, max: 36, step: 1 },
      deskHeightIn: { label: "Altura Inicial (Telescopica)", unit: "in", min: 24, max: 48, step: 1 },
      enableHTX: { label: "Bahia CoreXY HTX (1=Si, 0=No)", unit: "modo", min: 0, max: 1, step: 1 }
    },
    generateFusion: (p) => `# Prometheus CAD Suite — Mesa en L Desmontable 72x72 HTX Studio (V7.0)
import adsk.core, adsk.fusion, traceback, os, runpy

def run(context):
    try:
        app = adsk.core.Application.get()
        ui = app.userInterface
        master_path = r"C:\\Users\\Angel\\Documents\\Mis_Disenos_Fusion360\\01_Mesa_en_L_72x72_HTX\\MesaEnL_Fusion360_V8.py"
        if os.path.exists(master_path):
            exec(open(master_path, "r", encoding="utf-8-sig").read(), {"adsk": adsk, "app": app, "ui": ui, "__name__": "__PrometheusWeb__"})
        else:
            if ui: ui.messageBox("Script no encontrado en: " + master_path)
    except Exception as e:
        if app: app.log("Error al generar Mesa en L: " + traceback.format_exc())
        raise e

run(None)`,
    generateBlender: (p) => `# Prometheus CAD Suite — Mesa en L Desmontable y Movible V2.0 (Blender 5.2)
import bpy, os, runpy
master_blender_script = r"C:\\Users\\Angel\\Documents\\Mis_Disenos_Fusion360\\01_Mesa_en_L_72x72_HTX\\MesaEnL_Blender.py"
if os.path.exists(master_blender_script):
    runpy.run_path(master_blender_script, run_name="__main__")
`,
    generateFreeCAD: (p) => `# Prometheus CAD Suite — Mesa en L para FreeCAD
import FreeCAD, Part
doc = FreeCAD.newDocument("Mesa_en_L_HTX")
box1 = Part.makeBox(1828.8, 711.2, 38.1)
box2 = Part.makeBox(711.2, 1117.6, 38.1, FreeCAD.Vector(0, 711.2, 0))
table = box1.fuse(box2)
Part.show(table)
doc.recompute()`,
    get3DPoints: (p) => {
      const L = (p.wingLengthIn || 72) * 0.0254;
      const D = (p.depthIn || 28) * 0.0254;
      const H = (p.deskHeightIn || 28) * 0.0254;
      const scale = 2.4;
      const ox = -L / 2;
      const oy = -L / 2;
      const oz = -H / 2;

      const topPts = [
        [0, 0, H],
        [0, L, H],
        [D, L, H],
        [D, D, H],
        [L, D, H],
        [L, 0, H]
      ];
      const botPts = topPts.map(([x, y, z]) => [x, y, z - 0.038]);

      const legPositions = [
        [0.05, 0.05],
        [0.05, L - 0.05],
        [D - 0.05, L - 0.05],
        [L - 0.05, 0.05],
        [L - 0.05, D - 0.05],
        [D - 0.05, 0.05]
      ];

      const v: [number, number, number][] = [];
      const e: [number, number][] = [];

      topPts.forEach(([x, y, z]) => {
        v.push([(x + ox) * scale, (z + oz) * scale, (y + oy) * scale]);
      });
      botPts.forEach(([x, y, z]) => {
        v.push([(x + ox) * scale, (z + oz) * scale, (y + oy) * scale]);
      });

      for (let i = 0; i < 6; i++) {
        e.push([i, (i + 1) % 6]);
        e.push([i + 6, ((i + 1) % 6) + 6]);
        e.push([i, i + 6]);
      }

      legPositions.forEach(([lx, ly]) => {
        const topIdx = v.length;
        v.push([(lx + ox) * scale, (H - 0.038 + oz) * scale, (ly + oy) * scale]);
        const botIdx = v.length;
        v.push([(lx + ox) * scale, (0 + oz) * scale, (ly + oy) * scale]);
        e.push([topIdx, botIdx]);
      });

      return { vertices: v, edges: e };
    }
  },
  {
    id: "l_bracket",
    name: "Soporte en L Reforzado (Bracket M4/M5)",
    description: "Soporte angular rígido para perfiles de aluminio y ensambles mecánicos con perforaciones de precisión.",
    category: "mechanical",
    defaultParams: {
      baseLength: 80,
      wallHeight: 60,
      width: 50,
      thickness: 6,
      holeDia: 5
    },
    paramLabels: {
      baseLength: { label: "Largo de Base", unit: "mm", min: 40, max: 150, step: 5 },
      wallHeight: { label: "Alto de Pared", unit: "mm", min: 30, max: 120, step: 5 },
      width: { label: "Ancho Total", unit: "mm", min: 25, max: 100, step: 5 },
      thickness: { label: "Espesor de Pared", unit: "mm", min: 3, max: 12, step: 1 },
      holeDia: { label: "Diámetro Perforaciones", unit: "mm", min: 3, max: 8, step: 0.5 }
    },
    generateFusion: (p) => `# Prometheus CAD Suite — Autodesk Fusion 360
# Pieza: Soporte en L Reforzado (${p.baseLength}x${p.wallHeight}x${p.width} mm)
import adsk.core, adsk.fusion, traceback

def run(context):
    ui = None
    try:
        app = adsk.core.Application.get()
        ui = app.userInterface
        design = app.activeProduct
        if not design: return
        rootComp = design.rootComponent
        sketches = rootComp.sketches
        extrudes = rootComp.features.extrudeFeatures

        # 0. Registrar variables en tabla de Parámetros f(x)
        try:
            set_param('Largo_Base', '${p.baseLength} mm', 'mm', 'Largo de base')
            set_param('Alto_Pared', '${p.wallHeight} mm', 'mm', 'Altura de pared')
            set_param('Ancho_Total', '${p.width} mm', 'mm', 'Ancho de la pieza')
            set_param('Espesor', '${p.thickness} mm', 'mm', 'Espesor estructural')
            set_param('Diametro_Tornillo', '${p.holeDia} mm', 'mm', 'Diametro perforaciones')
        except: pass

        # 1. Perfil L en XY (dimensiones en cm: /10)
        xyPlane = rootComp.xYConstructionPlane
        sketch = sketches.add(xyPlane)
        lines = sketch.sketchCurves.sketchLines
        bL, wH, th = ${p.baseLength}/10.0, ${p.wallHeight}/10.0, ${p.thickness}/10.0
        lines.addByTwoPoints(adsk.core.Point3D.create(0, 0, 0), adsk.core.Point3D.create(bL, 0, 0))
        lines.addByTwoPoints(adsk.core.Point3D.create(bL, 0, 0), adsk.core.Point3D.create(bL, th, 0))
        lines.addByTwoPoints(adsk.core.Point3D.create(bL, th, 0), adsk.core.Point3D.create(th, th, 0))
        lines.addByTwoPoints(adsk.core.Point3D.create(th, th, 0), adsk.core.Point3D.create(th, wH, 0))
        lines.addByTwoPoints(adsk.core.Point3D.create(th, wH, 0), adsk.core.Point3D.create(0, wH, 0))
        lines.addByTwoPoints(adsk.core.Point3D.create(0, wH, 0), adsk.core.Point3D.create(0, 0, 0))

        # 2. Extrusion (${p.width}mm = ${(p.width/10).toFixed(1)}cm)
        profile = sketch.profiles.item(0)
        extInput = extrudes.createInput(profile, adsk.fusion.FeatureOperations.NewBodyFeatureOperation)
        extInput.setDistanceExtent(False, adsk.core.ValueInput.createByReal(${p.width}/10.0))
        body = extrudes.add(extInput).bodies.item(0)

        # 3. Perforaciones en la base
        holeSketch = sketches.add(rootComp.xZConstructionPlane)
        c1_3d = adsk.core.Point3D.create((bL+th)/2.0, 0, (${p.width}/10.0)*0.3)
        c2_3d = adsk.core.Point3D.create((bL+th)/2.0, 0, (${p.width}/10.0)*0.7)
        holeSketch.sketchCurves.sketchCircles.addByCenterRadius(holeSketch.modelToSketchSpace(c1_3d), (${p.holeDia}/20.0))
        holeSketch.sketchCurves.sketchCircles.addByCenterRadius(holeSketch.modelToSketchSpace(c2_3d), (${p.holeDia}/20.0))

        for prof in holeSketch.profiles:
            cutInput = extrudes.createInput(prof, adsk.fusion.FeatureOperations.CutFeatureOperation)
            cutInput.participantBodies = [body]
            try:
                cutInput.setDistanceExtent(False, adsk.core.ValueInput.createByReal(-1.0))
                extrudes.add(cutInput)
            except:
                cutInput.setDistanceExtent(False, adsk.core.ValueInput.createByReal(1.0))
                extrudes.add(cutInput)
        if app: app.log("¡Soporte en L modelado con exito en Fusion 360!")
    except Exception as e:
        if app: app.log("Error: " + traceback.format_exc())
        raise e

run(None)`,
    generateBlender: (p) => `# Prometheus CAD Suite — Blender 3D (bpy)
# Pieza: Soporte en L Reforzado (${p.baseLength}x${p.wallHeight}x${p.width} mm)
import bpy, bmesh

for obj in list(bpy.data.objects):
    if obj.type == 'MESH': bpy.data.objects.remove(obj, do_unlink=True)

mesh = bpy.data.meshes.new("Soporte_Prometheus")
obj = bpy.data.objects.new("Soporte_Prometheus", mesh)
bpy.context.collection.objects.link(obj)
bpy.context.view_layer.objects.active = obj
obj.select_set(True)

bm = bmesh.new()
bL, wH, w, th = ${p.baseLength}/1000.0, ${p.wallHeight}/1000.0, ${p.width}/1000.0, ${p.thickness}/1000.0
v0 = bm.verts.new((0.0, 0.0, 0.0))
v1 = bm.verts.new((bL,  0.0, 0.0))
v2 = bm.verts.new((bL,  0.0, th))
v3 = bm.verts.new((th,  0.0, th))
v4 = bm.verts.new((th,  0.0, wH))
v5 = bm.verts.new((0.0, 0.0, wH))

face = bm.faces.new([v0, v1, v2, v3, v4, v5])
extr = bmesh.ops.extrude_face_region(bm, geom=[face])
verts_move = [v for v in extr['geom'] if isinstance(v, bmesh.types.BMVert)]
bmesh.ops.translate(bm, vec=(0.0, w, 0.0), verts=verts_move)
bmesh.ops.recalc_face_normals(bm, faces=bm.faces)
bm.to_mesh(mesh)
bm.free()

bpy.ops.object.origin_set(type='ORIGIN_GEOMETRY', center='BOUNDS')
obj.location = (0, 0, 0)

bevel = obj.modifiers.new(name="Bisel", type='BEVEL')
bevel.width = 0.0015
bevel.segments = 2
bpy.ops.object.shade_smooth()

mat = bpy.data.materials.new(name="Aluminio_Prometheus")
mat.use_nodes = True
bsdf = mat.node_tree.nodes.get("Principled BSDF")
if bsdf:
    bsdf.inputs['Base Color'].default_value = (0.05, 0.55, 0.85, 1.0)
    bsdf.inputs['Metallic'].default_value = 0.95
    bsdf.inputs['Roughness'].default_value = 0.2
obj.data.materials.append(mat)

for area in bpy.context.screen.areas:
    if area.type == 'VIEW_3D':
        for space in area.spaces:
            if space.type == 'VIEW_3D': space.shading.type = 'MATERIAL'
        try:
            override = bpy.context.copy()
            override['area'] = area
            bpy.ops.view3d.view_selected(override)
        except: pass`,
    generateFreeCAD: (p) => `# Prometheus CAD Suite — FreeCAD Python
# Pieza: Soporte en L Paramétrico Exportable a STEP
import FreeCAD, Part

doc = FreeCAD.newDocument("Soporte_Prometheus")
bL, wH, w, th = ${p.baseLength}, ${p.wallHeight}, ${p.width}, ${p.thickness}

# Perfil L
wire = Part.makePolygon([
    FreeCAD.Vector(0, 0, 0),
    FreeCAD.Vector(bL, 0, 0),
    FreeCAD.Vector(bL, th, 0),
    FreeCAD.Vector(th, th, 0),
    FreeCAD.Vector(th, wH, 0),
    FreeCAD.Vector(0, wH, 0),
    FreeCAD.Vector(0, 0, 0)
])
face = Part.Face(wire)
solid = face.extrude(FreeCAD.Vector(0, 0, w))

# Agujeros de fijación
h1 = Part.makeCylinder(${p.holeDia}/2.0, th * 2, FreeCAD.Vector((bL+th)/2.0, -th/2.0, w*0.3), FreeCAD.Vector(0, 1, 0))
h2 = Part.makeCylinder(${p.holeDia}/2.0, th * 2, FreeCAD.Vector((bL+th)/2.0, -th/2.0, w*0.7), FreeCAD.Vector(0, 1, 0))
pieza_final = solid.cut(h1).cut(h2)

Part.show(pieza_final)
doc.recompute()
# Para exportar: Part.export([doc.ActiveObject], "Soporte_Prometheus.step")`,
    get3DPoints: (p) => {
      const bL = p.baseLength / 25;
      const wH = p.wallHeight / 25;
      const w = p.width / 25;
      const th = p.thickness / 25;
      const v: [number, number, number][] = [
        [-bL/2, -wH/2, -w/2],
        [bL/2, -wH/2, -w/2],
        [bL/2, -wH/2 + th, -w/2],
        [-bL/2 + th, -wH/2 + th, -w/2],
        [-bL/2 + th, wH/2, -w/2],
        [-bL/2, wH/2, -w/2],
        // Front face (Z = +w/2)
        [-bL/2, -wH/2, w/2],
        [bL/2, -wH/2, w/2],
        [bL/2, -wH/2 + th, w/2],
        [-bL/2 + th, -wH/2 + th, w/2],
        [-bL/2 + th, wH/2, w/2],
        [-bL/2, wH/2, w/2]
      ];
      const e: [number, number][] = [
        [0,1],[1,2],[2,3],[3,4],[4,5],[5,0],
        [6,7],[7,8],[8,9],[9,10],[10,11],[11,6],
        [0,6],[1,7],[2,8],[3,9],[4,10],[5,11]
      ];
      return { vertices: v, edges: e };
    }
  },
  {
    id: "esp32_case",
    name: "Carcasa Ventilada ESP32 / Sensores (Clima Tropical)",
    description: "Caja estanca con ranuras de convección térmica para microcontroladores y sensores en ambientes cálidos de Puerto Rico.",
    category: "enclosure",
    defaultParams: {
      boxLength: 90,
      boxWidth: 60,
      boxHeight: 35,
      wallThick: 2.5,
      ventSlots: 6
    },
    paramLabels: {
      boxLength: { label: "Largo Interior", unit: "mm", min: 60, max: 150, step: 5 },
      boxWidth: { label: "Ancho Interior", unit: "mm", min: 40, max: 100, step: 5 },
      boxHeight: { label: "Alto Total", unit: "mm", min: 20, max: 60, step: 5 },
      wallThick: { label: "Grosor de Pared", unit: "mm", min: 1.6, max: 4, step: 0.2 },
      ventSlots: { label: "Ranuras de Ventilación", unit: "qty", min: 3, max: 12, step: 1 }
    },
    generateFusion: (p) => `# Prometheus CAD Suite — Gabinete ESP32 para Fusion 360
import adsk.core, adsk.fusion, traceback

def run(context):
    try:
        app = adsk.core.Application.get()
        design = app.activeProduct
        root = design.rootComponent
        sketches = root.sketches
        extrudes = root.features.extrudeFeatures

        L, W, H, th = ${p.boxLength}/10.0, ${p.boxWidth}/10.0, ${p.boxHeight}/10.0, ${p.wallThick}/10.0
        sk = sketches.add(root.xYConstructionPlane)
        sk.sketchCurves.sketchLines.addTwoPointRectangle(adsk.core.Point3D.create(-L/2, -W/2, 0), adsk.core.Point3D.create(L/2, W/2, 0))
        prof = sk.profiles.item(0)
        extInput = extrudes.createInput(prof, adsk.fusion.FeatureOperations.NewBodyFeatureOperation)
        extInput.setDistanceExtent(False, adsk.core.ValueInput.createByReal(H))
        body = extrudes.add(extInput).bodies.item(0)

        # Vaciado (Shell)
        shells = root.features.shellFeatures
        faces = adsk.core.ObjectCollection.create()
        for f in body.faces:
            if f.geometry.surfaceType == adsk.core.SurfaceTypes.PlaneSurfaceType and f.geometry.normal.z > 0.9:
                faces.add(f)
        shellInput = shells.createInput(faces, False)
        shellInput.insideThickness = adsk.core.ValueInput.createByReal(th)
        shells.add(shellInput)
    except: pass
run(None)`,
    generateBlender: (p) => `# Prometheus CAD Suite — Carcasa ESP32 en Blender
import bpy

for obj in list(bpy.data.objects):
    if obj.type == 'MESH': bpy.data.objects.remove(obj, do_unlink=True)

L, W, H, th = ${p.boxLength}/1000.0, ${p.boxWidth}/1000.0, ${p.boxHeight}/1000.0, ${p.wallThick}/1000.0
bpy.ops.mesh.primitive_cube_add(size=1.0)
box = bpy.context.active_object
box.dimensions = (L, W, H)
bpy.ops.object.transform_apply(scale=True)

# Vaciado interior con Solidify
solid = box.modifiers.new(name="Grosor", type='SOLIDIFY')
solid.thickness = -th

mat = bpy.data.materials.new(name="PETG_Gabinete")
mat.use_nodes = True
bsdf = mat.node_tree.nodes.get("Principled BSDF")
if bsdf:
    bsdf.inputs['Base Color'].default_value = (0.1, 0.1, 0.12, 1.0)
    bsdf.inputs['Roughness'].default_value = 0.35
box.data.materials.append(mat)`,
    generateFreeCAD: (p) => `# Prometheus FreeCAD — Carcasa ESP32
import FreeCAD, Part
doc = FreeCAD.newDocument("Carcasa_ESP32")
L, W, H, th = ${p.boxLength}, ${p.boxWidth}, ${p.boxHeight}, ${p.wallThick}
outer = Part.makeBox(L, W, H)
inner = Part.makeBox(L - 2*th, W - 2*th, H, FreeCAD.Vector(th, th, th))
case = outer.cut(inner)
Part.show(case)
doc.recompute()`,
    get3DPoints: (p) => {
      const L = p.boxLength / 30;
      const W = p.boxWidth / 30;
      const H = p.boxHeight / 30;
      const v: [number, number, number][] = [
        [-L/2, -W/2, -H/2], [L/2, -W/2, -H/2], [L/2, W/2, -H/2], [-L/2, W/2, -H/2],
        [-L/2, -W/2, H/2], [L/2, -W/2, H/2], [L/2, W/2, H/2], [-L/2, W/2, H/2]
      ];
      const e: [number, number][] = [
        [0,1],[1,2],[2,3],[3,0],
        [4,5],[5,6],[6,7],[7,4],
        [0,4],[1,5],[2,6],[3,7]
      ];
      return { vertices: v, edges: e };
    }
  },
  {
    id: "nema17_mount",
    name: "Soporte de Precisión Motor NEMA 17",
    description: "Placa frontal para motor paso a paso con ranuras de centrado de 31mm y disipación de calor.",
    category: "robotics",
    defaultParams: {
      plateSize: 44,
      thickness: 5,
      collarDia: 23,
      boltSpacing: 31
    },
    paramLabels: {
      plateSize: { label: "Tamaño Placa", unit: "mm", min: 42, max: 60, step: 1 },
      thickness: { label: "Espesor", unit: "mm", min: 3, max: 8, step: 0.5 },
      collarDia: { label: "Diámetro Cuello", unit: "mm", min: 22, max: 26, step: 0.5 },
      boltSpacing: { label: "Paso Tornillería", unit: "mm", min: 30, max: 35, step: 0.5 }
    },
    generateFusion: (p) => `# Prometheus CAD — NEMA 17 Mount para Fusion 360
import adsk.core, adsk.fusion

def run(context):
    app = adsk.core.Application.get()
    root = app.activeProduct.rootComponent
    sk = root.sketches.add(root.xYConstructionPlane)
    size = ${p.plateSize}/10.0
    sk.sketchCurves.sketchLines.addTwoPointRectangle(adsk.core.Point3D.create(-size/2, -size/2, 0), adsk.core.Point3D.create(size/2, size/2, 0))
    # Orificio central
    sk.sketchCurves.sketchCircles.addByCenterRadius(adsk.core.Point3D.create(0, 0, 0), ${p.collarDia}/20.0)
    # Perforaciones M3
    d = ${p.boltSpacing}/20.0
    for x in [-d, d]:
        for y in [-d, d]:
            sk.sketchCurves.sketchCircles.addByCenterRadius(adsk.core.Point3D.create(x, y, 0), 0.17)
    ext = root.features.extrudeFeatures
    extInput = ext.createInput(sk.profiles.item(0), adsk.fusion.FeatureOperations.NewBodyFeatureOperation)
    extInput.setDistanceExtent(False, adsk.core.ValueInput.createByReal(${p.thickness}/10.0))
    ext.add(extInput)
run(None)`,
    generateBlender: (p) => `# Prometheus CAD — NEMA 17 en Blender
import bpy
bpy.ops.mesh.primitive_cube_add(size=1.0)
obj = bpy.context.active_object
obj.dimensions = (${p.plateSize}/1000.0, ${p.plateSize}/1000.0, ${p.thickness}/1000.0)
bpy.ops.object.transform_apply(scale=True)`,
    generateFreeCAD: (p) => `# Prometheus FreeCAD — NEMA 17
import FreeCAD, Part
doc = FreeCAD.newDocument("NEMA17_Mount")
box = Part.makeBox(${p.plateSize}, ${p.plateSize}, ${p.thickness}, FreeCAD.Vector(-${p.plateSize}/2, -${p.plateSize}/2, 0))
cyl = Part.makeCylinder(${p.collarDia}/2.0, ${p.thickness}*2, FreeCAD.Vector(0,0,-1))
final_part = box.cut(cyl)
Part.show(final_part)
doc.recompute()`,
    get3DPoints: (p) => {
      const s = p.plateSize / 20;
      const th = p.thickness / 20;
      const v: [number, number, number][] = [
        [-s/2, -s/2, -th/2], [s/2, -s/2, -th/2], [s/2, s/2, -th/2], [-s/2, s/2, -th/2],
        [-s/2, -s/2, th/2], [s/2, -s/2, th/2], [s/2, s/2, th/2], [-s/2, s/2, th/2]
      ];
      const e: [number, number][] = [
        [0,1],[1,2],[2,3],[3,0],
        [4,5],[5,6],[6,7],[7,4],
        [0,4],[1,5],[2,6],[3,7]
      ];
      return { vertices: v, edges: e };
    }
  },
  {
    id: "cilindro_mecanico",
    name: "Cilindro & Buje Mecánico Hueco",
    description: "Cilindro o tubo paramétrico hueco con opción pasante (ambos extremos abiertos) o con fondo ciego para bujes y acoples.",
    category: "mechanical",
    defaultParams: {
      height: 50,
      outerDia: 40,
      wallThick: 3,
      baseThick: 0
    },
    paramLabels: {
      height: { label: "Altura Total", unit: "mm", min: 20, max: 120, step: 5 },
      outerDia: { label: "Diámetro Exterior", unit: "mm", min: 15, max: 80, step: 2 },
      wallThick: { label: "Espesor de Pared", unit: "mm", min: 1.5, max: 8, step: 0.5 },
      baseThick: { label: "Espesor de Fondo (0 = Pasante)", unit: "mm", min: 0, max: 20, step: 1 }
    },
    generateFusion: (p) => {
      const isPasante = (p.baseThick || 0) <= 0;
      return `# Prometheus CAD — Cilindro Hueco para Fusion 360
import adsk.core, adsk.fusion

def run(context):
    app = adsk.core.Application.get()
    design = app.activeProduct
    root = design.rootComponent
    extrudes = root.features.extrudeFeatures

    # 0. Registrar Parámetros f(x)
    try:
        set_param('Diametro_Exterior', '${p.outerDia} mm', 'mm', 'Diametro exterior')
        set_param('Altura_Total', '${p.height} mm', 'mm', 'Altura total')
        set_param('Espesor_Pared', '${p.wallThick} mm', 'mm', 'Grosor de pared')
        set_param('Espesor_Fondo', '${p.baseThick || 0} mm', 'mm', 'Espesor de base (0=pasante)')
    except: pass

    r_ext = (${p.outerDia}/20.0)
    r_int = ((${p.outerDia} - 2*${p.wallThick})/20.0)
    h = ${p.height}/10.0
    base = ${p.baseThick || 0}/10.0

    # 1. Boceto exterior en XY
    sk = root.sketches.add(root.xYConstructionPlane)
    sk.sketchCurves.sketchCircles.addByCenterRadius(adsk.core.Point3D.create(0,0,0), r_ext)
    extIn = extrudes.createInput(sk.profiles.item(0), adsk.fusion.FeatureOperations.NewBodyFeatureOperation)
    extIn.setDistanceExtent(False, adsk.core.ValueInput.createByReal(h))
    body = extrudes.add(extIn).bodies.item(0)

    # 2. Vaciado interior
    sk2 = root.sketches.add(root.xYConstructionPlane)
    sk2.sketchCurves.sketchCircles.addByCenterRadius(adsk.core.Point3D.create(0,0,0), r_int)
    cutIn = extrudes.createInput(sk2.profiles.item(0), adsk.fusion.FeatureOperations.CutFeatureOperation)
    cutIn.participantBodies = [body]

    ${isPasante ? `
    # Vaciado pasante completo (ambos extremos abiertos)
    cutIn.setDistanceExtent(False, adsk.core.ValueInput.createByReal(h * 1.1))
    ` : `
    # Vaciado con fondo ciego dejando ${p.baseThick}mm de base
    cutIn.setDistanceExtent(False, adsk.core.ValueInput.createByReal(h - base))
    `}
    extrudes.add(cutIn)

run(None)`;
    },
    generateBlender: (p) => {
      const isPasante = (p.baseThick || 0) <= 0;
      return `# Prometheus CAD — Cilindro Hueco en Blender
import bpy

for obj in list(bpy.data.objects):
    if obj.type == 'MESH': bpy.data.objects.remove(obj, do_unlink=True)

r_ext = ${p.outerDia}/2000.0
r_int = (${p.outerDia} - 2*${p.wallThick})/2000.0
h = ${p.height}/1000.0
base = ${p.baseThick || 0}/1000.0

# 1. Cilindro exterior
bpy.ops.mesh.primitive_cylinder_add(radius=r_ext, depth=h, location=(0, 0, h/2))
outer = bpy.context.active_object
outer.name = "Cilindro_Prometheus"

# 2. Vaciado interior con operacion booleana
${isPasante ? `
# Vaciado pasante de extremo a extremo
bpy.ops.mesh.primitive_cylinder_add(radius=r_int, depth=h * 1.2, location=(0, 0, h/2))
cutter = bpy.context.active_object
` : `
# Vaciado con fondo ciego dejando ${p.baseThick}mm de base
cut_h = h - base
bpy.ops.mesh.primitive_cylinder_add(radius=r_int, depth=cut_h * 1.05, location=(0, 0, base + cut_h/2))
cutter = bpy.context.active_object
`}

bool_mod = outer.modifiers.new(name="Corte_Hueco", type='BOOLEAN')
bool_mod.object = cutter
bool_mod.operation = 'DIFFERENCE'
bpy.context.view_layer.objects.active = outer
bpy.ops.object.modifier_apply(modifier="Corte_Hueco")
bpy.data.objects.remove(cutter, do_unlink=True)

bpy.ops.object.shade_smooth()
mat = bpy.data.materials.new(name="Aluminio_Prometheus")
mat.use_nodes = True
bsdf = mat.node_tree.nodes.get("Principled BSDF")
if bsdf:
    bsdf.inputs['Base Color'].default_value = (0.05, 0.55, 0.85, 1.0)
    bsdf.inputs['Metallic'].default_value = 0.9
    bsdf.inputs['Roughness'].default_value = 0.25
outer.data.materials.append(mat)
`;
    },
    generateFreeCAD: (p) => {
      const isPasante = (p.baseThick || 0) <= 0;
      return `# Prometheus FreeCAD — Cilindro Hueco
import FreeCAD, Part
doc = FreeCAD.newDocument("Cilindro_Prometheus")
outer = Part.makeCylinder(${p.outerDia}/2.0, ${p.height})
${isPasante ? `
inner = Part.makeCylinder((${p.outerDia} - 2*${p.wallThick})/2.0, ${p.height} + 2, FreeCAD.Vector(0,0,-1))
` : `
inner = Part.makeCylinder((${p.outerDia} - 2*${p.wallThick})/2.0, ${p.height} - ${p.baseThick}, FreeCAD.Vector(0,0,${p.baseThick}))
`}
final_part = outer.cut(inner)
Part.show(final_part)
doc.recompute()`;
    },
    get3DPoints: (p) => {
      const r = p.outerDia / 25;
      const h = p.height / 25;
      const numSegments = 12;
      const v: [number, number, number][] = [];
      const e: [number, number][] = [];
      for (let i = 0; i < numSegments; i++) {
        const theta = (i * 2 * Math.PI) / numSegments;
        v.push([r * Math.cos(theta), -h/2, r * Math.sin(theta)]);
        v.push([r * Math.cos(theta), h/2, r * Math.sin(theta)]);
        const b = i * 2;
        const nextB = ((i + 1) % numSegments) * 2;
        e.push([b, nextB]);
        e.push([b + 1, nextB + 1]);
        e.push([b, b + 1]);
      }
      return { vertices: v, edges: e };
    }
  }
];

export interface EngineeringCADHubProps {
  onTransferToFaceplate?: (data: { widthMm: number; heightMm: number; title: string; subtitle: string; source: string }) => void;
  incomingDimensions?: { lengthMm: number; widthMm: number; heightMm: number; label?: string } | null;
}

export const EngineeringCADHub: React.FC<EngineeringCADHubProps> = ({ 
  onTransferToFaceplate, 
  incomingDimensions 
}) => {
  // Navigation / Target CAD selector
  const [selectedPlatform, setSelectedPlatform] = useState<"fusion360" | "blender" | "freecad" | "kicad" | "bambu">("fusion360");
  const [selectedPresetId, setSelectedPresetId] = useState<string>(CAD_PRESETS[0].id);
  const [modelParams, setModelParams] = useState<Record<string, number>>(CAD_PRESETS[0].defaultParams);

  // Active Engineering Projects Awareness
  const [projects, setProjects] = useState<any[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");

  useEffect(() => {
    const loadProjects = () => {
      try {
        const raw = localStorage.getItem("prometheus_engineering_projects");
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            setProjects(parsed);
            const savedActive = localStorage.getItem("prometheus_active_project_id");
            if (savedActive && parsed.some((p: any) => p.id === savedActive)) {
              setSelectedProjectId(savedActive);
            } else if (parsed.length > 0 && !selectedProjectId) {
              setSelectedProjectId(parsed[0].id);
            }
          }
        }
      } catch (e) {
        console.error("Error loading projects in CAD Hub", e);
      }
    };
    loadProjects();
    window.addEventListener("prometheus-project-updated", loadProjects);
    return () => window.removeEventListener("prometheus-project-updated", loadProjects);
  }, []);

  const activeProject = useMemo(() => {
    return projects.find((p) => p.id === selectedProjectId) || null;
  }, [projects, selectedProjectId]);

  // Sync incoming dimensions from 3D Scanner or Camera Vision Caliper
  useEffect(() => {
    if (incomingDimensions) {
      setModelParams((prev) => {
        const next = { ...prev };
        if ("boxLength" in next && incomingDimensions.lengthMm) next.boxLength = Math.round(incomingDimensions.lengthMm);
        if ("boxWidth" in next && incomingDimensions.widthMm) next.boxWidth = Math.round(incomingDimensions.widthMm);
        if ("boxHeight" in next && incomingDimensions.heightMm) next.boxHeight = Math.round(incomingDimensions.heightMm);
        if ("baseLength" in next && incomingDimensions.lengthMm) next.baseLength = Math.round(incomingDimensions.lengthMm);
        if ("wallHeight" in next && incomingDimensions.heightMm) next.wallHeight = Math.round(incomingDimensions.heightMm);
        if ("width" in next && incomingDimensions.widthMm) next.width = Math.round(incomingDimensions.widthMm);
        return next;
      });
      toast.success(`📐 Medidas aplicadas desde ${incomingDimensions.label || "Escáner Óptico"}: ${incomingDimensions.lengthMm} × ${incomingDimensions.widthMm} × ${incomingDimensions.heightMm} mm`);
    }
  }, [incomingDimensions]);

  // Live Bridge States
  const [fusionStatus, setFusionStatus] = useState<"checking" | "online" | "offline">("offline");
  const [blenderStatus, setBlenderStatus] = useState<"checking" | "online" | "offline">("offline");
  const [freecadStatus, setFreecadStatus] = useState<"checking" | "online" | "offline">("offline");
  const [isInjecting, setIsInjecting] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);

  // 1-Click transfer dimensions to 2D Faceplate (Google Pics Studio)
  const handleTransferToFaceplate = () => {
    let w = 100;
    let h = 70;
    if (modelParams.boxLength && modelParams.boxWidth) {
      w = modelParams.boxLength;
      h = modelParams.boxWidth;
    } else if (modelParams.baseLength && modelParams.width) {
      w = modelParams.baseLength;
      h = modelParams.width;
    } else if (modelParams.baseLength && modelParams.wallHeight) {
      w = modelParams.baseLength;
      h = modelParams.wallHeight;
    } else {
      const vals = Object.values(modelParams).filter((v) => typeof v === "number" && v >= 20);
      if (vals.length >= 2) {
        w = Math.max(...vals);
        h = vals.find((v) => v !== w) || 60;
      }
    }

    if (onTransferToFaceplate) {
      onTransferToFaceplate({
        widthMm: Math.round(w),
        heightMm: Math.round(h),
        title: currentPreset.name.toUpperCase(),
        subtitle: "PROMETHEUS CAD SUITE • 3D ENCLOSURE",
        source: currentPreset.name
      });
    }
  };

  // Aerospace Physical Telemetry State (Mass, Volume, Bodies, Fusion Document)
  interface CADTelemetry {
    bodiesCount?: number;
    volumeCm3?: number;
    massGrams?: number;
    documentName?: string;
    source?: "live_bridge" | "analytical";
  }
  const [liveTelemetry, setLiveTelemetry] = useState<CADTelemetry | null>(null);

  // 3D Canvas Viewport State
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [rotX, setRotX] = useState<number>(25);
  const [rotY, setRotY] = useState<number>(-35);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const dragStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const [materialLook, setMaterialLook] = useState<"titanium" | "petg" | "asa" | "steel">("titanium");

  // Bambu Slicer State
  const [bambuMaterial, setBambuMaterial] = useState<"petg" | "asa" | "pla" | "tpu">("petg");
  const [infillPercent, setInfillPercent] = useState<number>(25);
  const [bambuPrinterModel, setBambuPrinterModel] = useState<string>("Bambu Lab X1-Carbon / X2D");

  const currentPreset = useMemo(() => {
    return CAD_PRESETS.find((p) => p.id === selectedPresetId) || CAD_PRESETS[0];
  }, [selectedPresetId]);

  // When changing preset, update default params and reset live telemetry
  const handlePresetChange = (id: string) => {
    setSelectedPresetId(id);
    setLiveTelemetry(null);
    const p = CAD_PRESETS.find((x) => x.id === id);
    if (p) setModelParams(p.defaultParams);
  };

  // Material densities in g/cm³
  const MATERIAL_DENSITIES: Record<"titanium" | "petg" | "asa" | "steel", number> = {
    titanium: 4.43, // Ti-6Al-4V Grado 5 aeroespacial
    petg: 1.27,     // Filamento PETG industrial
    asa: 1.07,      // Filamento ASA anti-UV y salitre
    steel: 7.85     // Acero Inoxidable 316L
  };

  // Analytical Telemetry (real-time estimate from parametric mathematical volume)
  const analyticalTelemetry = useMemo(() => {
    const density = MATERIAL_DENSITIES[materialLook] || 2.7;
    let volMm3 = 0;

    if (currentPreset.id === "l_bracket") {
      const bL = modelParams.baseLength || 80;
      const wH = modelParams.wallHeight || 60;
      const w = modelParams.width || 50;
      const th = modelParams.thickness || 6;
      const holeDia = modelParams.holeDia || 5;
      const baseVol = bL * w * th;
      const wallVol = (wH - th) * w * th;
      const holesVol = 2 * Math.PI * Math.pow(holeDia / 2, 2) * th;
      volMm3 = Math.max(0, baseVol + wallVol - holesVol);
    } else if (currentPreset.id === "esp32_case") {
      const L = modelParams.boxLength || 90;
      const W = modelParams.boxWidth || 60;
      const H = modelParams.boxHeight || 35;
      const th = modelParams.wallThick || 2.5;
      const outer = L * W * H;
      const inner = Math.max(0, (L - 2 * th) * (W - 2 * th) * (H - th));
      volMm3 = Math.max(0, outer - inner);
    } else if (currentPreset.id === "nema17_mount") {
      const s = modelParams.plateSize || 44;
      const th = modelParams.thickness || 5;
      const cDia = modelParams.collarDia || 23;
      const plateVol = s * s * th;
      const collarVol = Math.PI * Math.pow(cDia / 2, 2) * th;
      const boltsVol = 4 * Math.PI * Math.pow(3.4 / 2, 2) * th;
      volMm3 = Math.max(0, plateVol - collarVol - boltsVol);
    } else if (currentPreset.id === "cilindro_mecanico") {
      const h = modelParams.height || 50;
      const rExt = (modelParams.outerDia || 40) / 2;
      const th = modelParams.wallThick || 3;
      const base = modelParams.baseThick || 0;
      const rInt = Math.max(0.5, rExt - th);
      const outerVol = Math.PI * Math.pow(rExt, 2) * h;
      const cutH = base > 0 ? Math.max(0, h - base) : h;
      const innerVol = Math.PI * Math.pow(rInt, 2) * cutH;
      volMm3 = Math.max(0, outerVol - innerVol);
    } else {
      volMm3 = 45000;
    }

    const volumeCm3 = volMm3 / 1000.0;
    const massGrams = volumeCm3 * density;
    return { volumeCm3, massGrams, density };
  }, [currentPreset.id, modelParams, materialLook]);

  // Ping Fusion 360, Blender, and FreeCAD Bridges (strictly localhost to avoid adblocker ERR_BLOCKED_BY_CLIENT)
  const checkBridges = async (silent = false) => {
    if (!silent) {
      setFusionStatus("checking");
      setBlenderStatus("checking");
      setFreecadStatus("checking");
    }

    // Ping Fusion 360 (Port 8765)
    try {
      const fRes = await fetch("http://localhost:8765/ping", { method: "GET", signal: AbortSignal.timeout(1200) });
      setFusionStatus(fRes.ok ? "online" : "offline");
    } catch {
      setFusionStatus("offline");
    }

    // Ping Blender (Port 8081)
    try {
      const bRes = await fetch("http://localhost:8081/ping", { method: "GET", signal: AbortSignal.timeout(1200) });
      setBlenderStatus(bRes.ok ? "online" : "offline");
    } catch {
      setBlenderStatus("offline");
    }

    // Ping FreeCAD (Port 8082)
    try {
      const fcRes = await fetch("http://localhost:8082/ping", { method: "GET", signal: AbortSignal.timeout(1200) });
      setFreecadStatus(fcRes.ok ? "online" : "offline");
    } catch {
      setFreecadStatus("offline");
    }
  };

  useEffect(() => {
    // Check once upon component mount
    checkBridges();

    // Check automatically when user switches back to this browser window (no 15s interval spam)
    const handleWindowFocus = () => {
      checkBridges(true);
    };
    window.addEventListener("focus", handleWindowFocus);
    return () => {
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, []);

  // Generated Code based on active platform
  const generatedCode = useMemo(() => {
    switch (selectedPlatform) {
      case "fusion360":
        return currentPreset.generateFusion(modelParams);
      case "blender":
        return currentPreset.generateBlender(modelParams);
      case "freecad":
        return currentPreset.generateFreeCAD(modelParams);
      case "kicad":
        return `# Prometheus KiCad Python (pcbnew)
# Generador de Placa y Huellas para PCB
import pcbnew

board = pcbnew.CreateEmptyBoard()
# Definir contorno de placa según parámetros
# Largo: ${modelParams.baseLength || modelParams.boxLength || 80}mm
# Ancho: ${modelParams.width || modelParams.boxWidth || 50}mm
print("Placa KiCad generada exitosamente por Prometheus Lab AI")`;
      case "bambu":
        return `; Prometheus CAM & Slicer Meta-Config
; Impresora: ${bambuPrinterModel}
; Material: ${bambuMaterial.toUpperCase()} (Perfil Puerto Rico Clima Tropical)
; Relleno: ${infillPercent}% Gyroid
; Boquilla: 0.4mm Hardened Steel
M104 S245 ; Temperatura boquilla
M140 S75  ; Temperatura cama
; Flujo volumétrico compensado contra humedad ambiental`;
    }
  }, [selectedPlatform, currentPreset, modelParams, bambuPrinterModel, bambuMaterial, infillPercent]);

  const postToBridge = async (port: number, code: string) => {
    const res = await fetch(`http://localhost:${port}/execute`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
      signal: AbortSignal.timeout(25000)
    });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({ message: `Error HTTP ${res.status}` }));
      throw new Error(errData.message || `Error del servidor (${res.status})`);
    }
    const data = await res.json();
    return data;
  };

  // Execute Live Bridge Action
  const handleInjectLive = async () => {
    setIsInjecting(true);
    if (selectedPlatform === "fusion360") {
      try {
        toast.info("⚡ Inyectando diseño en vivo a Autodesk Fusion 360...");
        const data = await postToBridge(8765, generatedCode);
        if (data.status === "ok") {
          if (data.telemetry) {
            setLiveTelemetry({ ...data.telemetry, source: "live_bridge" });
            toast.success(`¡Pieza inyectada! Masa: ${data.telemetry.massGrams}g | Vol: ${data.telemetry.volumeCm3} cm³ (${data.telemetry.bodiesCount} cuerpo)`);
          } else {
            toast.success("¡Pieza modelada en tiempo real en Autodesk Fusion 360!");
          }
        } else {
          toast.error(data.message || "Error en Fusion 360");
        }
      } catch (err: any) {
        toast.error("No se pudo conectar a Fusion 360 en el puerto 8765. Asegúrate de reiniciar el script con el código actualizado en Fusion.");
      } finally {
        setIsInjecting(false);
      }
    } else if (selectedPlatform === "blender") {
      try {
        toast.info("🎨 Inyectando diseño en vivo a Blender...");
        const data = await postToBridge(8081, generatedCode);
        if (data.status === "ok") {
          toast.success("¡Objeto generado y centrado en tiempo real en Blender!");
        } else {
          toast.error(data.message || "Error al ejecutar en Blender");
        }
      } catch (err: any) {
        toast.error("No se pudo conectar a Blender en el puerto 8081. Asegúrate de tener el script ejecutándose en Blender.");
      } finally {
        setIsInjecting(false);
      }
    } else if (selectedPlatform === "freecad") {
      try {
        toast.info("📐 Inyectando diseño en vivo a FreeCAD...");
        const data = await postToBridge(8082, generatedCode);
        if (data.status === "ok") {
          toast.success("¡Geometría y planos generados en tiempo real en FreeCAD!");
        } else {
          toast.error(data.message || "Error al ejecutar en FreeCAD");
        }
      } catch (err: any) {
        toast.error("No se pudo conectar a FreeCAD en el puerto 8082. Asegúrate de tener el script ejecutándose en FreeCAD.");
      } finally {
        setIsInjecting(false);
      }
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(generatedCode);
    setCopied(true);
    toast.success("Código copiado al portapapeles");
    setTimeout(() => setCopied(false), 2000);
  };

  // Save CAD design specifications into Active Project's living journal / files
  const handleSaveToProject = () => {
    if (!activeProject) {
      toast.error("Selecciona un proyecto primero para vincular este modelo 3D.");
      return;
    }
    try {
      const raw = localStorage.getItem("prometheus_engineering_projects");
      const list = raw ? JSON.parse(raw) : [];
      const updated = list.map((p: any) => {
        if (p.id === activeProject.id) {
          const attachments = p.attachments || [];
          const newAttachment = {
            id: `cad_${Date.now()}`,
            name: `${currentPreset.name} (${selectedPlatform.toUpperCase()})`,
            type: "cad_model",
            platform: selectedPlatform,
            presetId: currentPreset.id,
            params: modelParams,
            codeSnippet: generatedCode.substring(0, 300) + "...",
            date: new Date().toLocaleDateString("es-PR", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
          };
          return {
            ...p,
            attachments: [newAttachment, ...attachments]
          };
        }
        return p;
      });
      localStorage.setItem("prometheus_engineering_projects", JSON.stringify(updated));
      setProjects(updated);
      window.dispatchEvent(new Event("prometheus-project-updated"));
      toast.success(`💾 ¡Modelo "${currentPreset.name}" guardado en la bitácora de "${activeProject.title}"!`);
    } catch (e) {
      toast.error("Error al guardar el modelo en el proyecto.");
    }
  };

  // Consult Prometeo in Chat about this CAD geometry and tolerances
  const handleConsultPrometheusInChat = () => {
    const prompt = `Hola Prometeo, estoy en Conexiones CAD/CAM trabajando en el modelo "${currentPreset.name}" para el proyecto "${activeProject ? activeProject.title : "General"}". 
Plataforma: ${selectedPlatform.toUpperCase()}.
Parámetros actuales: ${JSON.stringify(modelParams)}.
¿Qué tolerancias de contracción para filamento PETG/ASA me recomiendas en Puerto Rico y cómo refuerzo los puntos de mayor fatiga mecánica?`;

    window.dispatchEvent(new CustomEvent("start-chat", {
      detail: {
        characterId: "prometeo",
        initialMessage: prompt
      }
    }));
    toast.success("Abriendo consulta en el Chat de Prometeo...");
  };

  const downloadFile = (content: string, filename: string) => {
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Archivo "${filename}" descargado con éxito`);
  };

  // 3D Canvas Rendering
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    ctx.clearRect(0, 0, width, height);

    // Subtle background grid in Canvas
    ctx.strokeStyle = "rgba(120, 140, 170, 0.12)";
    ctx.lineWidth = 1;
    for (let i = 0; i < width; i += 24) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, height);
      ctx.stroke();
    }
    for (let j = 0; j < height; j += 24) {
      ctx.beginPath();
      ctx.moveTo(0, j);
      ctx.lineTo(width, j);
      ctx.stroke();
    }

    const radX = (rotX * Math.PI) / 180;
    const radY = (rotY * Math.PI) / 180;

    const { vertices, edges } = currentPreset.get3DPoints(modelParams);

    // Project 3D to 2D
    const projected: { x: number; y: number }[] = vertices.map(([x, y, z]) => {
      // Rotate around Y
      const x1 = x * Math.cos(radY) + z * Math.sin(radY);
      const z1 = -x * Math.sin(radY) + z * Math.cos(radY);

      // Rotate around X
      const y2 = y * Math.cos(radX) - z1 * Math.sin(radX);
      const z2 = y * Math.sin(radX) + z1 * Math.cos(radX);

      // Perspective projection
      const scale = 320 / (z2 + 350);
      const px = width / 2 + x1 * scale * 25;
      const py = height / 2 - y2 * scale * 25;
      return { x: px, y: py };
    });

    // Material Styling
    let strokeColor = "#0ea5e9";
    let glowColor = "rgba(14, 165, 233, 0.4)";
    if (materialLook === "titanium") {
      strokeColor = "#38bdf8";
      glowColor = "rgba(56, 189, 248, 0.5)";
    } else if (materialLook === "petg") {
      strokeColor = "#10b981";
      glowColor = "rgba(16, 185, 129, 0.4)";
    } else if (materialLook === "asa") {
      strokeColor = "#f59e0b";
      glowColor = "rgba(245, 158, 11, 0.4)";
    } else {
      strokeColor = "#a855f7";
      glowColor = "rgba(168, 85, 247, 0.4)";
    }

    // Draw Edges with glow
    ctx.shadowBlur = 8;
    ctx.shadowColor = glowColor;
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 2;

    edges.forEach(([i, j]) => {
      if (projected[i] && projected[j]) {
        ctx.beginPath();
        ctx.moveTo(projected[i].x, projected[i].y);
        ctx.lineTo(projected[j].x, projected[j].y);
        ctx.stroke();
      }
    });

    // Draw vertex joints
    ctx.shadowBlur = 0;
    ctx.fillStyle = strokeColor;
    projected.forEach((p) => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 2.5, 0, Math.PI * 2);
      ctx.fill();
    });

  }, [rotX, rotY, currentPreset, modelParams, materialLook]);

  // Drag to rotate 3D canvas
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    setRotY((prev) => prev + dx * 0.7);
    setRotX((prev) => Math.max(-80, Math.min(80, prev + dy * 0.7)));
    dragStartRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseUp = () => setIsDragging(false);

  return (
    <div className="space-y-6 max-w-7xl mx-auto font-mono-tech animate-in fade-in duration-300">
      
      <PhotogrammetryBridge />

      {/* Top Banner: Status of Live Bridges & Multi-CAD Ecosystem */}
      <div className="bg-card/70 border border-border/80 rounded-2xl p-4 md:p-5 shadow-sm backdrop-blur-sm">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
                Hub de Fabricación Digital 2.4
              </span>
              <span className="text-[10px] text-muted-foreground">
                Fusion 360 • Blender • FreeCAD • KiCad • Bambu Studio
              </span>
            </div>
            <h3 className="text-xl md:text-2xl font-bold font-serif-display text-foreground mt-1">
              Conexiones CAD / CAM & <span className="text-primary italic font-normal">Puentes en Vivo</span>
            </h3>
            <p className="text-xs text-muted-foreground font-sans mt-0.5 max-w-2xl">
              Diseña con asistencia de IA y transfiere sólidos paramétricos directamente a tu software favorito sin copiar ni pegar archivos manualmente.
            </p>
          </div>

          {/* Connection Status Badges */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Fusion 360 Badge */}
            <div className="flex items-center gap-2 bg-muted/50 border border-border px-3 py-1.5 rounded-xl text-xs">
              <div className="flex items-center gap-1.5">
                <div className={`w-2.5 h-2.5 rounded-full ${fusionStatus === "online" ? "bg-emerald-500 animate-pulse" : fusionStatus === "checking" ? "bg-amber-500" : "bg-rose-500"}`} />
                <span className="font-bold text-[11px]">Fusion 360:</span>
              </div>
              <span className={`text-[10px] font-semibold ${fusionStatus === "online" ? "text-emerald-500" : "text-muted-foreground"}`}>
                {fusionStatus === "online" ? "🟢 En Vivo (8765)" : "🔴 Desconectado"}
              </span>
              <a
                href="/bridges/fusion360_prometheus_bridge.py"
                download="fusion360_prometheus_bridge.py"
                className="ml-1 text-[10px] text-primary hover:underline flex items-center gap-0.5"
                title="Descargar Add-In para Fusion 360"
              >
                <Download className="w-3 h-3" /> Add-In
              </a>
            </div>

            {/* Blender Badge */}
            <div className="flex items-center gap-2 bg-muted/50 border border-border px-3 py-1.5 rounded-xl text-xs">
              <div className="flex items-center gap-1.5">
                <div className={`w-2.5 h-2.5 rounded-full ${blenderStatus === "online" ? "bg-emerald-500 animate-pulse" : blenderStatus === "checking" ? "bg-amber-500" : "bg-rose-500"}`} />
                <span className="font-bold text-[11px]">Blender:</span>
              </div>
              <span className={`text-[10px] font-semibold ${blenderStatus === "online" ? "text-emerald-500" : "text-muted-foreground"}`}>
                {blenderStatus === "online" ? "🟢 En Vivo (8081)" : "🔴 Desconectado"}
              </span>
              <a
                href="/bridges/blender_prometheus_bridge.py"
                download="blender_prometheus_bridge.py"
                className="ml-1 text-[10px] text-primary hover:underline flex items-center gap-0.5"
                title="Descargar Add-On para Blender"
              >
                <Download className="w-3 h-3" /> Add-On
              </a>
            </div>

            {/* FreeCAD Badge */}
            <div className="flex items-center gap-2 bg-muted/50 border border-border px-3 py-1.5 rounded-xl text-xs">
              <div className="flex items-center gap-1.5">
                <div className={`w-2.5 h-2.5 rounded-full ${freecadStatus === "online" ? "bg-sky-500 animate-pulse" : freecadStatus === "checking" ? "bg-amber-500" : "bg-rose-500"}`} />
                <span className="font-bold text-[11px]">FreeCAD:</span>
              </div>
              <span className={`text-[10px] font-semibold ${freecadStatus === "online" ? "text-sky-500" : "text-muted-foreground"}`}>
                {freecadStatus === "online" ? "🟢 En Vivo (8082)" : "🔴 Desconectado"}
              </span>
              <a
                href="/bridges/freecad_prometheus_bridge.py"
                download="freecad_prometheus_bridge.py"
                className="ml-1 text-[10px] text-primary hover:underline flex items-center gap-0.5"
                title="Descargar Script/Macro para FreeCAD"
              >
                <Download className="w-3 h-3" /> Script
              </a>
            </div>

            {/* WeatherNext 3 Ambient Moisture Watch */}
            <div className="flex items-center gap-1.5 bg-sky-500/10 border border-sky-500/30 px-2.5 py-1.5 rounded-xl text-[10px] text-sky-400 font-bold" title="Humedad relativa estimada por WeatherNext 3 para impresión 3D en Puerto Rico">
              <Droplets className="w-3 h-3 text-sky-400" />
              <span>WeatherNext: 78% RH (PETG Seco)</span>
            </div>

            <button
              onClick={() => checkBridges()}
              className="p-2 rounded-xl bg-card border border-border hover:bg-muted text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              title="Volver a verificar puertos locales"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Project Awareness & Sync Bar */}
      <div className="bg-muted/40 border border-border/80 rounded-2xl p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-sm backdrop-blur-sm">
        <div className="flex items-center gap-2.5 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
            <FolderKanban className="w-4 h-4 text-amber-500" />
            <span>Proyecto Asignado:</span>
          </div>
          <select
            value={selectedProjectId}
            onChange={(e) => {
              setSelectedProjectId(e.target.value);
              localStorage.setItem("prometheus_active_project_id", e.target.value);
            }}
            className="text-xs bg-card border border-border rounded-xl px-3 py-1.5 font-bold text-foreground focus:outline-none focus:ring-2 focus:ring-amber-500/50 shadow-inner"
          >
            <option value="">-- Modo General (Sin Proyecto) --</option>
            {projects.map((proj) => (
              <option key={proj.id} value={proj.id}>
                {proj.title} ({proj.discipline})
              </option>
            ))}
          </select>
          {activeProject && (
            <span className="text-[10.5px] px-2.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 font-extrabold border border-amber-500/30">
              {activeProject.discipline}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleSaveToProject}
            disabled={!activeProject}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
              activeProject 
                ? "bg-amber-500 hover:bg-amber-600 text-black shadow-md font-extrabold" 
                : "bg-muted text-muted-foreground cursor-not-allowed border border-border"
            }`}
            title={activeProject ? "Guardar especificación y código de este modelo en la bitácora del proyecto" : "Selecciona un proyecto primero"}
          >
            <Bookmark className="w-3.5 h-3.5" /> Guardar en Bitácora del Proyecto
          </button>
          <button
            type="button"
            onClick={handleConsultPrometheusInChat}
            className="px-3.5 py-1.5 rounded-xl text-xs font-bold bg-primary hover:bg-primary/90 text-primary-foreground flex items-center gap-1.5 transition-all cursor-pointer shadow-md"
            title="Abrir consulta con Prometeo sobre este modelo y tolerancias"
          >
            <MessageSquare className="w-3.5 h-3.5" /> Consultar con Prometeo
          </button>
        </div>
      </div>

      {/* Main CAD Studio Layout: Left (Controls + 3D Viewport) | Right (Dispatcher & Code) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Left Column: Preset Selector + 3D Interactive Viewport + Parameters */}
        <div className="lg:col-span-5 space-y-4">
          
          {/* Preset Selector */}
          <div className="bg-card border border-border rounded-xl p-3.5 space-y-3 shadow-sm">
            <label className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider block">
              1. Selecciona el Modelo Paramétrico
            </label>
            <div className="grid grid-cols-1 gap-1.5">
              {CAD_PRESETS.map((p) => {
                const isSelected = selectedPresetId === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => handlePresetChange(p.id)}
                    className={`p-2.5 rounded-lg border text-left transition-all cursor-pointer flex items-start gap-2.5 ${
                      isSelected
                        ? "bg-primary/10 border-primary text-foreground shadow-sm"
                        : "bg-muted/30 border-border/60 hover:bg-muted/60 text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Box className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                    <div className="min-w-0">
                      <div className="text-xs font-bold truncate">{p.name}</div>
                      <div className="text-[9.5px] text-muted-foreground font-sans line-clamp-1">{p.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 3D Interactive WebGL/Canvas Viewport */}
          <div className="bg-card border border-border rounded-xl p-3.5 space-y-2 shadow-sm relative overflow-hidden">
            <div className="flex items-center justify-between border-b border-border/40 pb-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <Eye className="w-3.5 h-3.5 text-primary" /> Previsualizador 3D Web
              </span>
              <div className="flex items-center gap-1">
                {(["titanium", "petg", "asa", "steel"] as const).map((m) => (
                  <button
                    key={m}
                    onClick={() => setMaterialLook(m)}
                    className={`px-1.5 py-0.5 rounded text-[8px] uppercase font-bold transition-all cursor-pointer ${
                      materialLook === m
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>

            <div className="relative w-full h-[220px] rounded-lg bg-void/80 border border-border/60 flex items-center justify-center overflow-hidden">
              <canvas
                ref={canvasRef}
                width={440}
                height={220}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                className="w-full h-full cursor-grab active:cursor-grabbing"
              />
              <div className="absolute bottom-2 left-2 text-[8.5px] text-muted-foreground/80 pointer-events-none bg-card/80 px-2 py-0.5 rounded backdrop-blur-sm border border-border/40">
                Arrastra con el mouse para rotar en 360°
              </div>
            </div>
          </div>

          {/* Ficha de Telemetría Física Aeroespacial (Masa, Volumen, B-Rep Bodies) */}
          <div className="bg-gradient-to-br from-card/90 via-card/70 to-primary/5 border border-border/80 rounded-xl p-3.5 space-y-2.5 shadow-sm">
            <div className="flex items-center justify-between border-b border-border/40 pb-2">
              <div className="flex items-center gap-2">
                <Scale className="w-3.5 h-3.5 text-primary" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-foreground">
                  Ficha de Telemetría Física
                </span>
              </div>
              <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider border ${
                liveTelemetry?.source === "live_bridge"
                  ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/30 animate-pulse"
                  : "bg-muted text-muted-foreground border-border/60"
              }`}>
                {liveTelemetry?.source === "live_bridge" ? "⚡ En Vivo de Fusion 360" : "📐 Estimación Analítica"}
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="bg-muted/40 border border-border/50 rounded-lg p-2">
                <div className="text-[8.5px] uppercase font-bold text-muted-foreground">Masa Total</div>
                <div className="text-xs font-bold font-mono text-primary mt-0.5">
                  {(liveTelemetry?.massGrams ?? analyticalTelemetry.massGrams).toFixed(1)} <span className="text-[9.5px] font-normal text-muted-foreground">g</span>
                </div>
              </div>
              <div className="bg-muted/40 border border-border/50 rounded-lg p-2">
                <div className="text-[8.5px] uppercase font-bold text-muted-foreground">Volumen Sólido</div>
                <div className="text-xs font-bold font-mono text-foreground mt-0.5">
                  {(liveTelemetry?.volumeCm3 ?? analyticalTelemetry.volumeCm3).toFixed(2)} <span className="text-[9.5px] font-normal text-muted-foreground">cm³</span>
                </div>
              </div>
              <div className="bg-muted/40 border border-border/50 rounded-lg p-2">
                <div className="text-[8.5px] uppercase font-bold text-muted-foreground">Densidad</div>
                <div className="text-xs font-bold font-mono text-foreground mt-0.5">
                  {analyticalTelemetry.density.toFixed(2)} <span className="text-[9.5px] font-normal text-muted-foreground">g/cm³</span>
                </div>
              </div>
              <div className="bg-muted/40 border border-border/50 rounded-lg p-2">
                <div className="text-[8.5px] uppercase font-bold text-muted-foreground">Cuerpos B-Rep</div>
                <div className="text-xs font-bold font-mono text-foreground mt-0.5">
                  {liveTelemetry?.bodiesCount ?? 1} <span className="text-[9.5px] font-normal text-muted-foreground">Sólido</span>
                </div>
              </div>
            </div>

            <div className="text-[9px] text-muted-foreground flex items-center justify-between pt-1 border-t border-border/30">
              <span>{liveTelemetry?.documentName ? <>Doc Fusion: <strong className="text-foreground">{liveTelemetry.documentName}</strong></> : "Hermético 100% Manifold"}</span>
              <span className="text-emerald-500 font-semibold flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Listo para CNC / 3D
              </span>
            </div>
          </div>

          {/* Parametric Sliders */}
          <div className="bg-card border border-border rounded-xl p-3.5 space-y-3 shadow-sm">
            <label className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider flex items-center gap-1.5">
              <Sliders className="w-3.5 h-3.5 text-primary" /> Dimensiones & Tolerancias Milimétricas
            </label>
            <div className="space-y-2.5">
              {Object.entries(currentPreset.paramLabels).map(([key, config]) => {
                const val = modelParams[key] ?? currentPreset.defaultParams[key];
                return (
                  <div key={key} className="space-y-1">
                    <div className="flex justify-between items-center text-[10px]">
                      <span className="font-semibold text-foreground">{config.label}</span>
                      <span className="font-mono text-primary font-bold">
                        {val} {config.unit}
                      </span>
                    </div>
                    <input
                      type="range"
                      min={config.min}
                      max={config.max}
                      step={config.step}
                      value={val}
                      onChange={(e) => {
                        const nextVal = parseFloat(e.target.value);
                        setModelParams((prev) => ({ ...prev, [key]: nextVal }));
                      }}
                      className="w-full accent-primary h-1.5 bg-muted rounded-lg cursor-pointer"
                    />
                  </div>
                );
              })}
            </div>
          </div>

        </div>

        {/* Right Column: CAD/CAM Platform Tabs & Live Code Injection */}
        <div className="lg:col-span-7 space-y-4">
          
          {/* Target CAD / CAM Platform Tabs */}
          <div className="grid grid-cols-5 gap-1.5 bg-card/80 p-1.5 rounded-xl border border-border shadow-sm">
            {[
              { id: "fusion360", label: "Fusion 360", icon: Box, color: "text-amber-500" },
              { id: "blender", label: "Blender", icon: Sparkles, color: "text-orange-500" },
              { id: "freecad", label: "FreeCAD", icon: Layers, color: "text-sky-500" },
              { id: "kicad", label: "KiCad PCB", icon: Cpu, color: "text-emerald-500" },
              { id: "bambu", label: "Bambu / Orca", icon: Printer, color: "text-indigo-500" },
            ].map((plat) => {
              const isActive = selectedPlatform === plat.id;
              const IconP = plat.icon;
              return (
                <button
                  key={plat.id}
                  onClick={() => setSelectedPlatform(plat.id as any)}
                  className={`py-2 px-1.5 rounded-lg text-[10.5px] font-bold flex flex-col sm:flex-row items-center justify-center gap-1.5 transition-all cursor-pointer text-center ${
                    isActive
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`}
                >
                  <IconP className={`w-3.5 h-3.5 ${isActive ? "text-primary-foreground" : plat.color}`} />
                  <span className="truncate">{plat.label}</span>
                </button>
              );
            })}
          </div>

          {/* Action Toolbar: Live Inject + Copy + Download */}
          <div className="bg-card border border-border rounded-xl p-3.5 flex flex-wrap items-center justify-between gap-3 shadow-sm">
            <div className="flex items-center gap-2">
              {(selectedPlatform === "fusion360" || selectedPlatform === "blender") ? (
                <button
                  onClick={handleInjectLive}
                  disabled={isInjecting}
                  className={`px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2 cursor-pointer shadow-md transition-all ${
                    (selectedPlatform === "fusion360" && fusionStatus === "online") || (selectedPlatform === "blender" && blenderStatus === "online")
                      ? "bg-emerald-600 hover:bg-emerald-500 text-white shadow-[0_0_15px_rgba(16,185,129,0.3)]"
                      : "bg-primary hover:bg-primary/90 text-primary-foreground"
                  }`}
                >
                  <Play className={`w-3.5 h-3.5 ${isInjecting ? "animate-spin" : ""}`} />
                  {isInjecting
                    ? "Inyectando en Pantalla..."
                    : `⚡ Inyectar en ${selectedPlatform === "fusion360" ? "Fusion 360" : "Blender"} en Vivo`}
                </button>
              ) : selectedPlatform === "bambu" ? (
                <button
                  onClick={() => downloadFile(generatedCode, `${currentPreset.id}_bambu_project.3mf`)}
                  className="px-4 py-2 rounded-lg text-xs font-bold bg-emerald-600 hover:bg-emerald-500 text-white flex items-center gap-2 cursor-pointer shadow-md"
                >
                  <Printer className="w-3.5 h-3.5" />
                  Descargar Archivo .3MF (Bambu Studio / Orca)
                </button>
              ) : (
                <button
                  onClick={() => downloadFile(generatedCode, `${currentPreset.id}_${selectedPlatform}.py`)}
                  className="px-4 py-2 rounded-lg text-xs font-bold bg-sky-600 hover:bg-sky-500 text-white flex items-center gap-2 cursor-pointer shadow-md"
                >
                  <Download className="w-3.5 h-3.5" />
                  Descargar Script Paramétrico ({selectedPlatform})
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              {onTransferToFaceplate && (
                <button
                  type="button"
                  onClick={handleTransferToFaceplate}
                  className="px-3 py-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-500 dark:text-amber-400 text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-sm hover:shadow-[0_0_12px_rgba(245,158,11,0.25)]"
                  title="Transferir dimensiones a Google Pics Studio para diseñar la carátula o tapa frontal"
                >
                  <Palette className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Crear Carátula 2D</span>
                  <span className="sm:hidden">Carátula</span>
                </button>
              )}
              <button
                onClick={copyCode}
                className="px-3 py-1.5 rounded-lg border border-border bg-card hover:bg-muted text-xs text-foreground flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copiado" : "Copiar"}
              </button>
              <button
                onClick={() => downloadFile(generatedCode, `${currentPreset.id}_${selectedPlatform}.py`)}
                className="p-1.5 rounded-lg border border-border bg-card hover:bg-muted text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                title="Descargar archivo en disco"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Slicer Settings View when Bambu / OrcaSlicer is selected */}
          {selectedPlatform === "bambu" && (
            <div className="bg-muted/30 border border-border/80 rounded-xl p-4 space-y-3 font-mono-tech text-xs">
              <div className="flex items-center justify-between border-b border-border/40 pb-2">
                <span className="font-bold text-foreground flex items-center gap-1.5">
                  <Printer className="w-4 h-4 text-primary" /> Parámetros de Impresión Bambu Lab / OrcaSlicer
                </span>
                <span className="text-[10px] text-amber-600 dark:text-amber-400 font-semibold">
                  🇵🇷 Compensado para Humedad Tropical
                </span>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-[9px] uppercase font-bold text-muted-foreground block mb-1">Material:</label>
                  <select
                    value={bambuMaterial}
                    onChange={(e) => setBambuMaterial(e.target.value as any)}
                    className="w-full bg-card border border-border rounded-lg px-2.5 py-1.5 text-xs text-foreground"
                  >
                    <option value="petg">PETG Tropical (Recomendado)</option>
                    <option value="asa">ASA Anti-UV (Grado Marino)</option>
                    <option value="pla">PLA+ Alta Velocidad</option>
                    <option value="tpu">TPU 95A (Flexible / Estanco)</option>
                  </select>
                </div>

                <div>
                  <label className="text-[9px] uppercase font-bold text-muted-foreground block mb-1">Relleno (Infill):</label>
                  <select
                    value={infillPercent}
                    onChange={(e) => setInfillPercent(parseInt(e.target.value))}
                    className="w-full bg-card border border-border rounded-lg px-2.5 py-1.5 text-xs text-foreground"
                  >
                    <option value={15}>15% Gyroid (Ligero)</option>
                    <option value={25}>25% Gyroid (Equilibrado)</option>
                    <option value={45}>45% Gyroid (Alta Resistencia)</option>
                    <option value={100}>100% Sólido (Carga Mecánica)</option>
                  </select>
                </div>

                <div>
                  <label className="text-[9px] uppercase font-bold text-muted-foreground block mb-1">Impresora:</label>
                  <select
                    value={bambuPrinterModel}
                    onChange={(e) => setBambuPrinterModel(e.target.value)}
                    className="w-full bg-card border border-border rounded-lg px-2.5 py-1.5 text-xs text-foreground"
                  >
                    <option value="Bambu Lab X1-Carbon / X2D">Bambu Lab X1C / X2D</option>
                    <option value="Bambu Lab P1S">Bambu Lab P1S</option>
                    <option value="Bambu Lab A1">Bambu Lab A1</option>
                  </select>
                </div>
              </div>

              <div className="p-2.5 bg-card/60 rounded-lg border border-border/40 text-[9.5px] text-muted-foreground leading-relaxed">
                {bambuMaterial === "petg" && "💡 Nota PR: El PETG ofrece excelente tenacidad ante vibraciones. Secar a 65°C en el AMS para evitar burbujas por humedad."}
                {bambuMaterial === "asa" && "💡 Nota PR: El ASA es el material rey para intemperie en Puerto Rico; no se degrada con rayos UV ni con el salitre costero."}
                {bambuMaterial === "pla" && "💡 Nota PR: Fácil de imprimir a 300 mm/s. Evitar dejarlo dentro de vehículos cerrados bajo el sol para evitar deformaciones."}
                {bambuMaterial === "tpu" && "💡 Nota PR: Perfecto para juntas herméticas IP65 de gabinetes solares. Imprimir a velocidad baja (<30 mm/s)."}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-border/40">
                <span className="text-[10px] text-muted-foreground flex items-center gap-1 font-sans">
                  <Bot className="w-3.5 h-3.5 text-primary" /> Automatización con Agente Astra:
                </span>
                <button
                  type="button"
                  onClick={() => {
                    const directive = `# PROMETHEUS CAD SUITE — DIRECTIVA PARA AGENTE ASTRA (COMPUTER USE)
FocusWindow("BambuStudio.exe")
1. Atajo: 'Ctrl + 1' (Pestaña Prepare)
2. Clic en pieza: Presionar 'Ctrl + A' (Auto-orientar pieza con base plana a la cama)
3. Pestaña 'Strength': Wall loops = 4, Sparse Infill = ${infillPercent}%, Pattern = Gyroid (evita cruces y ruido)
4. Pestaña 'Support': Enable = True, Type = Tree (auto), Top Z distance = 0.20 mm (desprendimiento a mano)
5. Atajo: 'Ctrl + R' (Slice plate) y validar en vista Preview
6. Atajo: 'Ctrl + Shift + P' (Print plate / Enviar a Bambu Handy en iPhone 16 Pro Max)`;
                    navigator.clipboard.writeText(directive);
                    toast.success("Directiva de Astra para Bambu Studio copiada al portapapeles");
                  }}
                  className="px-2.5 py-1 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 text-[10.5px] font-bold flex items-center gap-1.5 transition-all cursor-pointer"
                >
                  <Bot className="w-3.5 h-3.5" />
                  <span>Copiar Directiva para Astra</span>
                </button>
              </div>
            </div>
          )}

          {/* Code Inspector Box */}
          <div className="bg-void rounded-xl border border-border/80 overflow-hidden shadow-inner flex flex-col">
            <div className="bg-card/90 border-b border-border/50 px-3.5 py-2 flex items-center justify-between text-xs">
              <span className="font-mono text-[10px] text-muted-foreground flex items-center gap-1.5">
                <FileCode className="w-3.5 h-3.5 text-primary" />
                {selectedPlatform === "fusion360" ? "fusion360_script.py (adsk.fusion)" :
                 selectedPlatform === "blender" ? "blender_script.py (bpy)" :
                 selectedPlatform === "freecad" ? "freecad_script.py (Part/FreeCAD)" :
                 selectedPlatform === "kicad" ? "kicad_layout.py (pcbnew)" : "bambu_profile.gcode"}
              </span>
              <span className="text-[9px] uppercase font-bold text-muted-foreground">
                {generatedCode.split("\n").length} Líneas
              </span>
            </div>

            <pre className="p-4 text-[10px] font-mono text-muted-foreground overflow-x-auto max-h-[360px] leading-relaxed select-all">
              <code>{generatedCode}</code>
            </pre>
          </div>

        </div>

      </div>

      {/* Puerto Rico Fabrication & Materials Guide Card */}
      <div className="bg-gradient-to-r from-card via-card/80 to-muted/40 border border-border/80 rounded-xl p-4 font-mono-tech text-xs space-y-2">
        <div className="flex items-center justify-between border-b border-border/20 pb-1.5">
          <span className="font-bold text-foreground flex items-center gap-1.5">
            🇵🇷 Consejos de Fabricación e Insumos en Puerto Rico
          </span>
          <span className="text-[9.5px] text-muted-foreground">
            Bambu Lab • Tornillería Inox 316 • Ferreterías Locales
          </span>
        </div>
        <div className="grid md:grid-cols-3 gap-3 pt-1 text-[10px] text-muted-foreground font-sans leading-relaxed">
          <div className="p-2.5 bg-card/60 rounded-lg border border-border/40">
            <strong className="text-foreground block mb-0.5 font-mono-tech text-[10.5px]">🌪️ Resiliencia y Salitre:</strong>
            Para exteriores o techos en PR, utiliza siempre tornillería de <strong>Acero Inoxidable 304/316</strong> disponible en Home Depot, National Lumber o ferreterías locales.
          </div>
          <div className="p-2.5 bg-card/60 rounded-lg border border-border/40">
            <strong className="text-foreground block mb-0.5 font-mono-tech text-[10.5px]">📦 Suministros por USPS:</strong>
            Filamentos especializados (ASA, PA-CF) y componentes de electrónica (ESP32, sensores) llegan con tarifa doméstica de EE.UU. sin aduanas desde Mouser, Digi-Key o Amazon.
          </div>
          <div className="p-2.5 bg-card/60 rounded-lg border border-border/40">
            <strong className="text-foreground block mb-0.5 font-mono-tech text-[10.5px]">⚡ Ahorro Eléctrico LUMA:</strong>
            Una Bambu Lab imprimiendo consume ~110W promedio. Con la tarifa actual ($0.28/kWh), una pieza de 4 horas cuesta apenas <strong>$0.12 USD</strong> de electricidad.
          </div>
        </div>
      </div>

    </div>
  );
};
